import { omit } from 'lodash';
import buildSearchLink from './buildSearchLink';
import {
	parseAmenityFromPath,
	getFilterValueForAmenity,
} from './parseAmenityFromPath';

/**
 * Search-context keys that describe WHERE the search happens, not WHAT is
 * being filtered. When redirecting an amenity LSA page back to its city CLP,
 * the city/state live in the destination pathname, so they must be stripped
 * from the query string to avoid a redundant ?city=...&state=... CLP URL.
 *
 * `amenities` is handled separately (surgically) so co-selected amenities are
 * preserved — only the amenity tied to the LSA path is removed.
 */
const CLP_CONTEXT_KEYS = [
	'city',
	'state',
	'location',
	'subPageType',
	'pageType',
	'amenity',
	'type',
	'vehicle',
	'page',
];

/**
 * The minimal set of params that pin down WHERE a search is anchored — as
 * opposed to what's being filtered or what page type it is. Kept separate
 * from CLP_CONTEXT_KEYS above (and from BASE_PARAM_KEYS in
 * SearchFiltersTray.js): those two lists mix in filter/page-type keys for
 * their own purposes (CLP_CONTEXT_KEYS strips pathname-encoded context when
 * redirecting to a CLP; BASE_PARAM_KEYS is the broader "survives Clear All"
 * set), so neither is a safe stand-in when the only intent is "replace the
 * search location, discarding every previous way that location was
 * expressed." Used when overriding location with a fresh lat/lng (e.g. a map
 * pan) — any of these left over from the previous search would ride along
 * and could take precedence over the new coordinates on the backend.
 */
export const LOCATION_IDENTITY_KEYS = [
	'city',
	'state',
	'latitude',
	'longitude',
	'zip',
	'locationSource',
];

/**
 * Decides where an "Apply Filters" / filter-change navigation should go.
 *
 * Most searches only mutate the query string (`{ search }`). But amenity LSA
 * pages (e.g. /self-storage/texas/austin/climate-controlled/) encode the
 * amenity in the URL *path*, not the query string. Clearing that amenity and
 * re-applying must change the *pathname* back to the city CLP — otherwise the
 * route re-seeds the amenity from the still-present path slug and the filter
 * appears stuck (SF-21011).
 *
 * @param {Object} args
 * @param {string} args.pathname  Current location pathname (pre-navigation).
 * @param {Object} args.params    Final, pickBy-cleaned search params to apply.
 * @param {string} [args.cityUrl] City CLP path from the store (e.g.
 *                                "/self-storage/texas/austin/").
 * @returns {{ search: string, pathname?: string }} A react-router navigate target.
 */
export default function resolveSearchNavigation({ pathname, params, cityUrl }) {
	const amenitySlug = parseAmenityFromPath(pathname);
	const amenityFilterKey = amenitySlug
		? getFilterValueForAmenity(amenitySlug)
		: null;

	// The boolean param is the reliable "is this amenity checked" signal shared
	// by both filter UIs (the new tray clears the boolean but leaves a stale
	// `amenities` array; the old modal re-derives clean params via the selector).
	// Redirect only when we're on an amenity LSA path, that amenity's boolean is
	// gone, and we have a CLP destination to navigate to.
	const amenityCleared =
		amenityFilterKey && !params[amenityFilterKey] && !!cityUrl;

	if (!amenityCleared) {
		return { search: buildSearchLink(params) };
	}

	// Drop context keys (city/state/amenity slug) since they're encoded in the
	// destination pathname; keep the remaining user-selected filters.
	const remainingFilters = omit(params, CLP_CONTEXT_KEYS);

	// Surgically remove the LSA amenity from the array (old FilterGroup UI),
	// preserving any co-selected amenities. Drop the array entirely if empty.
	if (Array.isArray(remainingFilters.amenities)) {
		const kept = remainingFilters.amenities.filter(
			(value) => value !== amenityFilterKey,
		);
		remainingFilters.amenities = kept.length ? kept : undefined;
	}

	const search = buildSearchLink(remainingFilters);

	return {
		pathname: cityUrl,
		// buildSearchLink returns "?" for an empty param set; normalize to ""
		// so the city CLP URL stays clean when no other filters remain.
		search: search === '?' ? '' : search,
	};
}
